import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useLayoutEffect } from 'react';
import { BetslipResolutionData, BetslipResolutionDataPayload } from '../@types';
import { QUERY_KEYS } from '../constants';
import { MessageBetType } from '../helpers/betslipMessageParser';
import { useBetBuilderFeature } from '../hooks/useBetBuilderFeature';
import { useInvalidateQuery } from '../hooks/useInvalidateQuery';
import useLocalization from '../hooks/useLocalization';
import useProductAvailability from '../hooks/useProductAvailability';
import useWebsocket, { WebsocketData } from '../hooks/useWebsocket';
import { useCalculateBetBuilder } from '../mutations/useCalculateBetBuilder';
import { useGlobalTicketConditions } from '../queries';
import {
  getBetslipState,
  subscribeToBetslipBets,
  teardownBetslipRuntime,
  useBetslipStore,
} from '../stores/betslip/betslipStore';
import type { BetslipRecord } from '../stores/betslip/persistence';

// Defined at module scope so the socket flush callback is stable and never re-subscribes.
const applyBetslipSocketMessages = (data: WebsocketData<MessageBetType>[]) =>
  getBetslipState().applySocketMessages(data);

const getBetslipEventIds = (bets: BetslipRecord) => new Set(Object.values(bets).map((bet) => bet.eventId));

/**
 * Headless companion of the betslip store: everything the betslip needs from React land lives
 * here — websocket wiring, `_BetSlip` room membership, bet-resolution notifications, and syncing
 * app context (auth, feature flags, ticket conditions) into the store.
 */
const BetslipBridge = () => {
  const { isAuthenticated, userId } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useLocalization();
  const invalidateData = useInvalidateQuery();
  const { isCasinoOnlyProduct } = useProductAvailability();
  const isBetBuilderEnabled = useBetBuilderFeature();
  const { data: globalTicketConditions } = useGlobalTicketConditions();
  const { mutateAsync: calculateBetBuilder } = useCalculateBetBuilder();

  const calculateBetBuilderAdapter = useCallback(
    (eventId: string, outcomeIds: string[]) => calculateBetBuilder({ eventId, payload: { selected_odds: outcomeIds } }),
    [calculateBetBuilder]
  );

  const { joinRoom, leaveRoom } = useWebsocket<MessageBetType>({ callback: applyBetslipSocketMessages });

  // Declarative `_BetSlip` room membership: rooms follow the events present in the betslip. The
  // initial sync joins rooms for bets rehydrated from localStorage; the subscription diff replaces
  // the imperative joinRoom/leaveRoom/resetWS calls scattered through the old provider actions.
  useEffect(() => {
    let joinedEventIds = new Set<string>();

    const syncRooms = (bets: BetslipRecord) => {
      const nextEventIds = getBetslipEventIds(bets);

      nextEventIds.forEach((eventId) => {
        if (!joinedEventIds.has(eventId)) {
          joinRoom(`${eventId}_BetSlip`);
        }
      });
      joinedEventIds.forEach((eventId) => {
        if (!nextEventIds.has(eventId)) {
          leaveRoom(`${eventId}_BetSlip`);
        }
      });

      joinedEventIds = nextEventIds;
    };

    syncRooms(getBetslipState().bets);

    return subscribeToBetslipBets(syncRooms);
  }, [joinRoom, leaveRoom]);

  // Store commands can run directly from browser events. Synchronize their React-owned inputs
  // before paint so no interaction observes the previous render's auth/config snapshot.
  useLayoutEffect(() => {
    getBetslipState().setAppContext({
      isAuthenticated,
      isBetBuilderEnabled,
      globalTicketConditions,
      calculateBetBuilder: calculateBetBuilderAdapter,
    });
  }, [isAuthenticated, isBetBuilderEnabled, globalTicketConditions, calculateBetBuilderAdapter]);

  useEffect(() => teardownBetslipRuntime, []);

  const betCount = useBetslipStore((state) => state.betCount);

  useEffect(() => {
    if (isAuthenticated && isCasinoOnlyProduct && betCount > 0) {
      getBetslipState().removeAllBets();
    }
  }, [isAuthenticated, isCasinoOnlyProduct, betCount]);

  const userIdRoomUpdaterCallback = useCallback(
    (data: BetslipResolutionData[]) => {
      data.forEach((message) => {
        const messageData = message[userId];

        if (!messageData) {
          return;
        }

        invalidateData([QUERY_KEYS.pendingBetslipsCount]);

        if (messageData.payload.acceptStatus === 'rejected') {
          if (messageData.payload?.reofferedId) {
            getBetslipState().addReofferedBettingSlipId(messageData.payload.reofferedId as string);
          } else {
            enqueueSnackbar(t('bettingSlipRejected'), {
              variant: 'error',
            });
          }
        } else if (messageData.payload.acceptStatus === 'accepted') {
          enqueueSnackbar(t('confirmed'), {
            variant: 'success',
          });
        }

        if (messageData.payload.acceptStatus === 'admin_cancelled') {
          enqueueSnackbar(t('bettingSlipRejected'), {
            variant: 'error',
          });

          invalidateData([QUERY_KEYS.balance, QUERY_KEYS.myBetsCount]);
        }
      });
    },
    [userId, enqueueSnackbar, t, invalidateData]
  );

  useWebsocket<BetslipResolutionDataPayload>({
    callback: userIdRoomUpdaterCallback,
  });

  return null;
};

export default BetslipBridge;
